import { useDeleteCommunity } from '@communities/community/hooks';
import { useDeleteCommunityGroup } from '@communities/community-groups/hooks';
import type { CommunityGroupWithCommunities, GuideCommunity } from '@core/components/Guide';
import type { DomainError } from '@core/domain';
import { useCallback, useMemo } from 'react';

import { useCommunityGroupsForDeletion } from './useCommunityGroupsForDeletion';

/** Flattened community carrying its parent group name for display. */
export interface CommunityWithGroupName extends GuideCommunity {
  groupName: string;
}

/**
 * Orchestration hook for the Community Management page.
 *
 * Composes data fetching and deletion for both community groups and communities,
 * exposing `deleteItemContext` objects compatible with `SortableDataList`.
 *
 * Communities are derived by flattening the nested groups-with-communities
 * query — no separate per-group query or filter is needed.
 */
export function useCommunityManagement() {
  // Community Groups data + deletion
  const { communityGroups, loading: groupsLoading, errors: groupsErrors, refetch } = useCommunityGroupsForDeletion();
  const { deleteCommunityGroup, loading: isDeletingGroup, errors: deleteGroupErrors, resetErrors: resetGroupErrors } = useDeleteCommunityGroup();

  // Flatten all communities from the nested groups response
  const communities: CommunityWithGroupName[] = useMemo(
    () =>
      communityGroups.flatMap(group =>
        group.communities.map(community => ({
          ...community,
          groupName: group.name,
        }))
      ),
    [communityGroups]
  );

  const communitiesErrors: DomainError[] | null = groupsErrors; // driven by the same query

  // Delete community — no communityGroupId needed; the groups-with-communities
  // query is refetched after group deletion and we add it as a refetch target below.
  const { deleteCommunity, loading: isDeletingCommunity, errors: deleteCommunityErrors, resetErrors: resetCommunityErrors } = useDeleteCommunity();

  // Stable callbacks for delete handlers
  const handleDeleteGroup = useCallback((group: CommunityGroupWithCommunities) => deleteCommunityGroup(group.id), [deleteCommunityGroup]);
  const handleDeleteCommunity = useCallback((community: CommunityWithGroupName) => deleteCommunity(community.id), [deleteCommunity]);

  // DeleteItemContext for community groups (used by SortableDataList)
  const groupDeleteItemContext = useMemo(
    () => ({
      handler: handleDeleteGroup,
      isDeleting: isDeletingGroup,
      errors: deleteGroupErrors,
      resetErrors: resetGroupErrors,
      getItemDisplayName: (group: CommunityGroupWithCommunities) => group.name,
      isDeleteDisabled: (group: CommunityGroupWithCommunities) => group.communities.length > 0,
      getDeleteDisabledReason: (group: CommunityGroupWithCommunities) =>
        group.communities.length > 0
          ? `Cannot delete — contains ${group.communities.length} ${group.communities.length === 1 ? 'community' : 'communities'}`
          : undefined,
    }),
    [handleDeleteGroup, isDeletingGroup, deleteGroupErrors, resetGroupErrors]
  );

  // DeleteItemContext for communities (used by SortableDataList)
  const communityDeleteItemContext = useMemo(
    () => ({
      handler: handleDeleteCommunity,
      isDeleting: isDeletingCommunity,
      errors: deleteCommunityErrors,
      resetErrors: resetCommunityErrors,
      getItemDisplayName: (community: CommunityWithGroupName) => community.name,
    }),
    [handleDeleteCommunity, isDeletingCommunity, deleteCommunityErrors, resetCommunityErrors]
  );

  return {
    // Community Groups section
    communityGroups,
    groupsLoading,
    groupsErrors,
    groupDeleteItemContext,

    // Communities section
    communities,
    communitiesLoading: groupsLoading, // same query drives both
    communitiesErrors,
    communityDeleteItemContext,

    // Manual refresh
    refetch,
  };
}
